import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../../firebase';
import { collection, onSnapshot, query, orderBy, doc, setDoc, deleteDoc, addDoc, updateDoc } from 'firebase/firestore';
import { ref as sRef, getDownloadURL, deleteObject } from 'firebase/storage';
import { uploadOptimizedImage } from '../../../services/imageOptimizer';
import { MainBannerModel, MainVideoSettingModel, COLLECTIONS } from '../../../models';
import { 
    Image as ImageIcon, Plus, Trash2, Edit2, Eye, EyeOff, 
    ArrowUp, ArrowDown, CheckCircle2, Loader2, 
    Upload, Link as LinkIcon, Sparkles, Sliders, X, Smartphone, Monitor,
    Youtube, Play, Save, Check, RefreshCw, AlertCircle
} from 'lucide-react';

const DEFAULT_BANNER_IMAGES = [
    { label: '학원 전경 배경', url: '/main-bg.jpg' },
    { label: '학원 입구 인테리어', url: '/academy_entrance.png' },
    { label: '1:1 밀착 수업', url: '/system_oneonone.jpg' },
    { label: '개인 맞춤 튜터링', url: '/system_tutoring.jpg' },
    { label: '전문 강사진', url: '/system_teacher.jpg' },
];

const getYoutubeEmbedUrl = (url?: string): string | null => {
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    const regExp = /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/;
    const match = trimmed.match(regExp);
    if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=0&rel=0`;
    }
    if (/^[\w-]{11}$/.test(trimmed)) {
        return `https://www.youtube.com/embed/${trimmed}?autoplay=0&rel=0`;
    }
    return null;
};

const BannersRoot: React.FC = () => {
    const [banners, setBanners] = useState<MainBannerModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState<MainBannerModel | null>(null);

    // Main Top YouTube Video State
    const [mainVideoUrl, setMainVideoUrl] = useState('');
    const [mainVideoTitle, setMainVideoTitle] = useState('정성우학원 강사 소개 영상');
    const [isMainVideoActive, setIsMainVideoActive] = useState(true);
    const [videoSaving, setVideoSaving] = useState(false);
    const [videoSaved, setVideoSaved] = useState(false);

    // Form state (Banner Management)
    const [title, setTitle] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [mobileImageUrl, setMobileImageUrl] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [buttonLink, setButtonLink] = useState('inquiry');
    const [order, setOrder] = useState(1);
    const [isActive, setIsActive] = useState(true);

    const [uploading, setUploading] = useState(false);
    const [imageInputMode, setImageInputMode] = useState<'upload' | 'preset' | 'url'>('upload');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const mobileFileInputRef = useRef<HTMLInputElement>(null);

    // Real-time Firestore sync for Main Video Setting
    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'settings', 'mainVideo'), (snap) => {
            if (snap.exists()) {
                const data = snap.data() as MainVideoSettingModel;
                setMainVideoUrl(data.youtubeUrl || '');
                setMainVideoTitle(data.title || '정성우학원 강사 소개 영상');
                setIsMainVideoActive(data.isActive ?? true);
                try {
                    localStorage.setItem('jsw_main_video_cache', JSON.stringify(data));
                } catch (e) {}
            } else {
                setMainVideoUrl('https://www.youtube.com/watch?v=1TAevjH74QM');
                setMainVideoTitle('정성우학원 강사 소개 영상');
                setIsMainVideoActive(true);
            }
        }, (err) => {
            console.error('Error fetching main video:', err);
        });
        return () => unsub();
    }, []);

    // Real-time Firestore sync for Banners
    useEffect(() => {
        const q = query(collection(db, COLLECTIONS.MAIN_BANNERS), orderBy('order', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: MainBannerModel[] = [];
            snapshot.forEach((docSnap) => {
                list.push({ id: docSnap.id, ...docSnap.data() } as MainBannerModel);
            });
            setBanners(list);
            setLoading(false);
        }, (err) => {
            console.error('Error fetching main banners:', err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleSaveMainVideo = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setVideoSaving(true);
        try {
            const videoData: MainVideoSettingModel = {
                youtubeUrl: mainVideoUrl.trim(),
                title: mainVideoTitle.trim() || '정성우학원 강사 소개 영상',
                isActive: isMainVideoActive,
                updatedAt: Date.now()
            };
            await setDoc(doc(db, 'settings', 'mainVideo'), videoData, { merge: true });

            try {
                localStorage.setItem('jsw_main_video_cache', JSON.stringify(videoData));
            } catch (e) {}

            setVideoSaved(true);
            setTimeout(() => setVideoSaved(false), 3000);
        } catch (err) {
            console.error('Error saving main video:', err);
            alert('유튜브 영상 설정 저장 중 오류가 발생했습니다.');
        } finally {
            setVideoSaving(false);
        }
    };

    const openAddModal = () => {
        setEditingBanner(null);
        setTitle(`메인 배너 #${banners.length + 1}`);
        setImageUrl('/main-bg.jpg');
        setMobileImageUrl('');
        setYoutubeUrl('');
        setButtonLink('inquiry');
        setOrder(banners.length > 0 ? Math.max(...banners.map(b => b.order || 0)) + 1 : 1);
        setIsActive(true);
        setImageInputMode('upload');
        setIsModalOpen(true);
    };

    const openEditModal = (banner: MainBannerModel) => {
        setEditingBanner(banner);
        setTitle(banner.title || `메인 배너 #${banner.order}`);
        setImageUrl(banner.imageUrl || '');
        setMobileImageUrl(banner.mobileImageUrl || '');
        setYoutubeUrl(banner.youtubeUrl || '');
        setButtonLink(banner.buttonLink || 'inquiry');
        setOrder(banner.order ?? 1);
        setIsActive(banner.isActive ?? true);
        setImageInputMode(banner.imageUrl?.startsWith('http') ? 'url' : 'preset');
        setIsModalOpen(true);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 업로드 가능합니다.');
            return;
        }

        setUploading(true);
        try {
            const fileName = `${Date.now()}_${file.name.replace(/\.[^/.]+$/, "")}.webp`;
            const storageRef = sRef(storage, `main-banners/${fileName}`);
            const snapshot = await uploadOptimizedImage(storageRef, file, { maxWidth: 1920, quality: 0.85 });
            const downloadUrl = await getDownloadURL(snapshot.ref);
            setImageUrl(downloadUrl);
        } catch (err) {
            console.error('Image upload failed:', err);
            alert('이미지 업로드 실패: ' + (err as Error).message);
        } finally {
            setUploading(false);
        }
    };

    const handleMobileFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 업로드 가능합니다.');
            return;
        }

        setUploading(true);
        try {
            const fileName = `mobile_${Date.now()}_${file.name.replace(/\.[^/.]+$/, "")}.webp`;
            const storageRef = sRef(storage, `main-banners/${fileName}`);
            const snapshot = await uploadOptimizedImage(storageRef, file, { maxWidth: 1080, quality: 0.85 });
            const downloadUrl = await getDownloadURL(snapshot.ref);
            setMobileImageUrl(downloadUrl);
        } catch (err) {
            console.error('Mobile image upload failed:', err);
            alert('모바일 이미지 업로드 실패: ' + (err as Error).message);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!imageUrl) {
            alert('배너 이미지를 선택하거나 업로드해 주세요.');
            return;
        }

        try {
            const bannerData: Omit<MainBannerModel, 'id'> = {
                title: title.trim() || `메인 배너 #${order}`,
                titleHighlight: '',
                subtitle: '',
                badgeText: '',
                imageUrl: imageUrl.trim(),
                mobileImageUrl: mobileImageUrl.trim(),
                youtubeUrl: youtubeUrl.trim(),
                buttonText: '',
                buttonLink: buttonLink.trim() || 'inquiry',
                order: Number(order) || 1,
                isActive,
                overlayOpacity: 0,
                createdAt: editingBanner ? editingBanner.createdAt : Date.now()
            };

            if (editingBanner) {
                await setDoc(doc(db, COLLECTIONS.MAIN_BANNERS, editingBanner.id), bannerData, { merge: true });
                alert('배너가 수정되었습니다.');
            } else {
                await addDoc(collection(db, COLLECTIONS.MAIN_BANNERS), bannerData);
                alert('새로운 메인 배너가 등록되었습니다.');
            }

            setIsModalOpen(false);
        } catch (err) {
            console.error('Error saving banner:', err);
            alert('배너 저장 중 오류가 발생했습니다.');
        }
    };

    const handleToggleActive = async (banner: MainBannerModel) => {
        try {
            await updateDoc(doc(db, COLLECTIONS.MAIN_BANNERS, banner.id), {
                isActive: !banner.isActive
            });
        } catch (err) {
            console.error('Toggle active failed:', err);
        }
    };

    const handleDelete = async (banner: MainBannerModel) => {
        if (!window.confirm(`'${banner.title}' 배너를 삭제하시겠습니까?`)) return;

        try {
            await deleteDoc(doc(db, COLLECTIONS.MAIN_BANNERS, banner.id));
            if (banner.imageUrl && banner.imageUrl.includes('firebasestorage.googleapis.com')) {
                try {
                    const storageRef = sRef(storage, banner.imageUrl);
                    await deleteObject(storageRef);
                } catch (sErr) {
                    console.warn('Storage delete warning:', sErr);
                }
            }
            alert('배너가 삭제되었습니다.');
        } catch (err) {
            console.error('Delete banner failed:', err);
            alert('삭제에 실패했습니다.');
        }
    };

    const handleMoveOrder = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= banners.length) return;

        const currentBanner = banners[index];
        const targetBanner = banners[targetIndex];

        try {
            await updateDoc(doc(db, COLLECTIONS.MAIN_BANNERS, currentBanner.id), { order: targetBanner.order });
            await updateDoc(doc(db, COLLECTIONS.MAIN_BANNERS, targetBanner.id), { order: currentBanner.order });
        } catch (err) {
            console.error('Reorder failed:', err);
        }
    };

    const embedPreviewUrl = getYoutubeEmbedUrl(mainVideoUrl);

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between shrink-0 sticky top-0 z-20 shadow-sm">
                <div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                        <Sliders className="text-indigo-600" size={26} />
                        메인 배너 및 영상 관리
                    </h1>
                    <p className="text-sm text-slate-500 font-medium mt-1">
                        홈페이지 메인 상단의 대표 유튜브 영상과 배너 이미지 슬라이드를 관리합니다.
                    </p>
                </div>
                <button
                    onClick={openAddModal}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold text-sm rounded-xl shadow-lg shadow-indigo-200 transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                    <Plus size={18} />
                    새 배너 등록
                </button>
            </div>

            {/* Content Area */}
            <div className="p-8 max-w-7xl mx-auto w-full space-y-8">
                
                {/* 1. Main YouTube Video Management Section */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden transition-all">
                    <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 border-b border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-red-500/20 text-red-400 border border-red-500/30 flex items-center justify-center shrink-0 shadow-inner">
                                <Youtube size={26} />
                            </div>
                            <div>
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-500/20 text-red-300 font-bold text-xs mb-1">
                                    <Play size={12} className="fill-current" /> 메인 대표 영상
                                </div>
                                <h3 className="text-lg font-black tracking-tight text-white">홈페이지 메인 대표 유튜브 영상</h3>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 border ${
                                isMainVideoActive && embedPreviewUrl
                                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                    : 'bg-slate-700/50 text-slate-400 border-slate-600/50'
                            }`}>
                                {isMainVideoActive && embedPreviewUrl ? <Eye size={13} /> : <EyeOff size={13} />}
                                {isMainVideoActive && embedPreviewUrl ? '홈페이지 노출 중' : '홈페이지 비노출'}
                            </span>
                        </div>
                    </div>

                    <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Video Settings Input Area */}
                        <div className="lg:col-span-6 space-y-5 flex flex-col justify-between">
                            <div className="space-y-4">
                                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                                    홈페이지 메인 최상단(히어로 영역)에 16:9 와이드스크린으로 노출되는 대표 유튜브 영상입니다. 유튜브 링크를 입력하시면 즉시 실시간으로 메인 화면에 반영됩니다.
                                </p>

                                <div>
                                    <label className="block text-xs font-extrabold text-slate-700 mb-1.5 uppercase tracking-wider">
                                        유튜브 영상 링크 URL <span className="text-rose-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute left-3.5 top-3.5 text-red-500 pointer-events-none">
                                            <Youtube size={18} />
                                        </div>
                                        <input
                                            type="text"
                                            value={mainVideoUrl}
                                            onChange={(e) => setMainVideoUrl(e.target.value)}
                                            placeholder="예: https://www.youtube.com/watch?v=1TAevjH74QM 또는 https://youtu.be/..."
                                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all font-mono"
                                        />
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                                        💡 일반 영상 주소(watch?v=...), 단축 주소(youtu.be/...), 쇼츠(shorts/...) 모두 지원합니다.
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-xs font-extrabold text-slate-700 mb-1.5 uppercase tracking-wider">
                                        영상 제목 (선택)
                                    </label>
                                    <input
                                        type="text"
                                        value={mainVideoTitle}
                                        onChange={(e) => setMainVideoTitle(e.target.value)}
                                        placeholder="예: 정성우학원 강사 소개 영상"
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:outline-none focus:border-indigo-500 transition-all"
                                    />
                                </div>

                                <div className="pt-2">
                                    <label className="flex items-center gap-3 p-3.5 bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 cursor-pointer transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={isMainVideoActive}
                                            onChange={(e) => setIsMainVideoActive(e.target.checked)}
                                            className="w-5 h-5 accent-red-600 rounded cursor-pointer"
                                        />
                                        <div>
                                            <span className="text-xs font-bold text-slate-800 block">
                                                홈페이지 메인 상단에 영상 노출하기
                                            </span>
                                            <span className="text-[11px] text-slate-400">
                                                체크 해제 시 메인 홈페이지에서 영상 영역이 숨겨지고 배너 슬라이드가 상단에 위치합니다.
                                            </span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="pt-4 border-t border-slate-100 flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => handleSaveMainVideo()}
                                    disabled={videoSaving}
                                    className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 font-bold text-sm rounded-xl shadow-md transition-all ${
                                        videoSaved 
                                            ? 'bg-emerald-600 text-white' 
                                            : 'bg-red-600 hover:bg-red-700 active:bg-red-800 text-white shadow-red-200'
                                    }`}
                                >
                                    {videoSaving ? (
                                        <>
                                            <Loader2 className="animate-spin" size={18} />
                                            저장 중...
                                        </>
                                    ) : videoSaved ? (
                                        <>
                                            <Check size={18} />
                                            성공적으로 저장되었습니다!
                                        </>
                                    ) : (
                                        <>
                                            <Save size={18} />
                                            유튜브 영상 설정 저장
                                        </>
                                    )}
                                </button>
                                {mainVideoUrl && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (window.confirm('유튜브 영상 링크를 초기화하시겠습니까?')) {
                                                setMainVideoUrl('');
                                            }
                                        }}
                                        className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-xs rounded-xl transition-colors shrink-0"
                                    >
                                        지우기
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Video Live Preview Area */}
                        <div className="lg:col-span-6 flex flex-col">
                            <label className="text-xs font-extrabold text-slate-700 mb-2 uppercase tracking-wider flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <Monitor size={14} className="text-red-600" />
                                    실시간 영상 미리보기 (16:9 와이드스크린)
                                </span>
                                {embedPreviewUrl && (
                                    <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                                        연결 정상
                                    </span>
                                )}
                            </label>
                            
                            <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 shadow-xl flex items-center justify-center group">
                                {embedPreviewUrl ? (
                                    <iframe
                                        src={embedPreviewUrl}
                                        title={mainVideoTitle || '유튜브 영상 미리보기'}
                                        className="w-full h-full border-0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                ) : (
                                    <div className="text-center p-6 space-y-3">
                                        <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 text-slate-500 flex items-center justify-center mx-auto">
                                            <Youtube size={28} />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-sm font-bold text-slate-300">유튜브 영상이 입력되지 않았습니다</p>
                                            <p className="text-xs text-slate-500 max-w-xs mx-auto">
                                                좌측에 유튜브 링크를 입력하시면 여기에 실시간 영상 플레이어가 표시됩니다.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Banner Image Slider Information Banner */}
                <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 text-white rounded-2xl p-6 shadow-xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6 border border-indigo-500/20">
                    <div className="space-y-2 max-w-2xl relative z-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 font-bold text-xs">
                            <Sparkles size={14} /> 메인 배너 이미지 가이드
                        </div>
                        <h3 className="text-xl font-bold">등록하신 배너 이미지가 메인 상단에서 슬라이드로 노출됩니다.</h3>
                        <p className="text-sm text-slate-300 font-normal leading-relaxed">
                            순서(Order)가 빠른 배너가 먼저 노출되며, 활성화(Active) 상태인 배너들이 6초 간격으로 자동 슬라이드 전환됩니다.
                        </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-3 bg-white/10 backdrop-blur-md px-4 py-3 rounded-xl border border-white/15 text-xs text-indigo-200">
                        <CheckCircle2 size={18} className="text-emerald-400" />
                        <span>활성화된 배너: <strong className="text-white text-sm">{banners.filter(b => b.isActive).length}개</strong> / 전체 {banners.length}개</span>
                    </div>
                </div>

                {/* Banner List */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-slate-400 space-y-4">
                        <Loader2 className="animate-spin text-indigo-600" size={40} />
                        <p className="text-sm font-medium">배너 목록을 가져오는 중입니다...</p>
                    </div>
                ) : banners.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center space-y-4 shadow-sm">
                        <div className="w-16 h-16 bg-indigo-50 rounded-2xl text-indigo-600 flex items-center justify-center mx-auto">
                            <ImageIcon size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-800">등록된 커스텀 배너가 없습니다</h3>
                        <p className="text-sm text-slate-500 max-w-md mx-auto">
                            현재는 정성우학원 기본 프리셋 배너가 홈페이지에 노출되고 있습니다. <br />
                            '새 배너 등록' 버튼을 눌러 제작된 학원 배너 이미지를 등록해 보세요!
                        </p>
                        <button
                            onClick={openAddModal}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-colors shadow-md"
                        >
                            <Plus size={16} /> 첫 배너 추가하기
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <h2 className="text-lg font-bold text-slate-800 flex items-center justify-between">
                            <span>등록된 배너 리스트 ({banners.length})</span>
                            <span className="text-xs font-normal text-slate-400">화살표 버튼으로 노출 순서를 변경할 수 있습니다.</span>
                        </h2>

                        <div className="grid grid-cols-1 gap-6">
                            {banners.map((banner, index) => (
                                <div
                                    key={banner.id}
                                    className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden shadow-sm hover:shadow-md flex flex-col lg:flex-row ${
                                        banner.isActive ? 'border-slate-200' : 'border-slate-200 opacity-60 bg-slate-50/50'
                                    }`}
                                >
                                    {/* Preview Thumbnail */}
                                    <div className="lg:w-80 h-48 lg:h-auto relative bg-slate-950 shrink-0 overflow-hidden group">
                                        <img
                                            src={banner.imageUrl}
                                            alt={banner.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = '/main-bg.jpg';
                                            }}
                                        />
                                        <div className="absolute inset-0 p-3 flex flex-col justify-between text-white pointer-events-none bg-gradient-to-t from-slate-950/70 via-transparent to-black/40">
                                            <div className="flex items-center justify-between">
                                                <span className="px-2.5 py-0.5 rounded-full bg-indigo-600/90 text-[10px] font-bold">
                                                    PC 배너
                                                </span>
                                                <span className="text-[10px] font-mono bg-black/60 px-2 py-0.5 rounded text-slate-300">
                                                    Order: #{banner.order}
                                                </span>
                                            </div>
                                            {banner.mobileImageUrl && (
                                                <div className="flex items-center gap-1 bg-black/70 px-2 py-0.5 rounded text-[10px] text-indigo-300 self-start">
                                                    <Smartphone size={12} /> 모바일 전용 이미지 포함
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Information & Action Details */}
                                    <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-bold font-mono">
                                                        #{banner.order}
                                                    </span>
                                                    <h3 className="text-base font-black text-slate-900 break-keep">
                                                        {banner.title}
                                                    </h3>
                                                </div>

                                                <button
                                                    onClick={() => handleToggleActive(banner)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors border ${
                                                        banner.isActive
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                                            : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                                                    }`}
                                                >
                                                    {banner.isActive ? <Eye size={14} /> : <EyeOff size={14} />}
                                                    {banner.isActive ? '노출 중' : '비노출'}
                                                </button>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 pt-1">
                                                <span className="inline-flex items-center gap-1 bg-slate-100 px-2.5 py-1 rounded-lg text-slate-600 font-medium">
                                                    <Monitor size={14} className="text-slate-400" /> PC 이미지 등록됨
                                                </span>
                                                {banner.mobileImageUrl ? (
                                                    <span className="inline-flex items-center gap-1 bg-indigo-50 px-2.5 py-1 rounded-lg text-indigo-700 font-semibold">
                                                        <Smartphone size={14} /> 모바일 이미지 등록됨
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 bg-slate-100 px-2.5 py-1 rounded-lg text-slate-400">
                                                        <Smartphone size={14} /> 모바일 기본 적용
                                                    </span>
                                                )}
                                                {banner.buttonLink && (
                                                    <span className="inline-flex items-center gap-1 bg-slate-100 px-2.5 py-1 rounded-lg text-slate-600 font-medium">
                                                        🔗 클릭 링크: {banner.buttonLink}
                                                    </span>
                                                )}
                                                {banner.youtubeUrl && (
                                                    <span className="inline-flex items-center gap-1 bg-red-50 px-2.5 py-1 rounded-lg text-red-600 font-medium border border-red-100">
                                                        <Youtube size={13} className="text-red-500" /> 유튜브 링크
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Bottom Control Bar */}
                                        <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleMoveOrder(index, 'up')}
                                                    disabled={index === 0}
                                                    className="p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30 rounded-lg transition-colors"
                                                    title="위로 이동"
                                                >
                                                    <ArrowUp size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleMoveOrder(index, 'down')}
                                                    disabled={index === banners.length - 1}
                                                    className="p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30 rounded-lg transition-colors"
                                                    title="아래로 이동"
                                                >
                                                    <ArrowDown size={16} />
                                                </button>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(banner)}
                                                    className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                                                >
                                                    <Edit2 size={14} /> 수정
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(banner)}
                                                    className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors border border-rose-100"
                                                >
                                                    <Trash2 size={14} /> 삭제
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Dialog for Add / Edit */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-3xl max-w-3xl w-full shadow-2xl overflow-hidden border border-slate-100 my-8 animate-in fade-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="bg-slate-900 text-white px-8 py-5 flex items-center justify-between border-b border-slate-800">
                            <div>
                                <h3 className="text-lg font-black tracking-tight flex items-center gap-2">
                                    <Sparkles className="text-indigo-400" size={20} />
                                    {editingBanner ? '메인 배너 이미지 수정' : '신규 메인 배너 이미지 등록'}
                                </h3>
                                <p className="text-xs text-slate-400 font-medium">홈페이지 메인 슬라이드에 노출될 배너 이미지 등록</p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[80vh] overflow-y-auto">
                            
                            {/* Pure Banner Image Preview */}
                            <div className="space-y-2">
                                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                                    <span>등록 배너 이미지 미리보기</span>
                                    <span className="text-indigo-600 font-medium text-xs">순수 배너 렌더링</span>
                                </label>
                                <div className="relative rounded-2xl overflow-hidden shadow-md bg-slate-950 h-52 sm:h-64 flex items-center justify-center border border-slate-800">
                                    <picture className="w-full h-full block">
                                        {mobileImageUrl && (
                                            <source media="(max-width: 640px)" srcSet={mobileImageUrl} />
                                        )}
                                        <img
                                            src={imageUrl || '/main-bg.jpg'}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = '/main-bg.jpg';
                                            }}
                                        />
                                    </picture>
                                </div>
                            </div>

                            {/* Image Selection Section */}
                            <div className="space-y-4 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                                <label className="text-sm font-bold text-slate-800 flex items-center justify-between">
                                    <span>PC 메인 배너 이미지 선택 <span className="text-rose-500">*</span></span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('upload')}
                                            className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${
                                                imageInputMode === 'upload' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200'
                                            }`}
                                        >
                                            파일 업로드
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('preset')}
                                            className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${
                                                imageInputMode === 'preset' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200'
                                            }`}
                                        >
                                            학원 프리셋 선택
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('url')}
                                            className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${
                                                imageInputMode === 'url' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200'
                                            }`}
                                        >
                                            URL 입력
                                        </button>
                                    </div>
                                </label>

                                {imageInputMode === 'upload' && (
                                    <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-indigo-200 hover:border-indigo-400 bg-white rounded-xl transition-colors cursor-pointer"
                                         onClick={() => fileInputRef.current?.click()}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                        />
                                        {uploading ? (
                                            <div className="flex items-center gap-2 text-indigo-600 font-bold text-sm py-2">
                                                <Loader2 className="animate-spin" size={20} /> 배너 이미지 스토리지 업로드 중...
                                            </div>
                                        ) : (
                                            <div className="text-center space-y-2">
                                                <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto">
                                                    <Upload size={20} />
                                                </div>
                                                <p className="text-sm font-bold text-slate-700">클릭하여 PC용 배너 이미지 업로드 (권장: 1920x1080 고화질)</p>
                                                <p className="text-xs text-slate-400">JPG, PNG, WebP 이미지 지원</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {imageInputMode === 'preset' && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-1">
                                        {DEFAULT_BANNER_IMAGES.map((preset) => (
                                            <button
                                                key={preset.url}
                                                type="button"
                                                onClick={() => setImageUrl(preset.url)}
                                                className={`p-2 rounded-xl border text-left flex flex-col gap-2 transition-all ${
                                                    imageUrl === preset.url
                                                        ? 'border-indigo-600 bg-indigo-50/50 ring-2 ring-indigo-500/20'
                                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                                }`}
                                            >
                                                <div className="h-20 w-full bg-slate-900 rounded-lg overflow-hidden relative">
                                                    <img src={preset.url} alt={preset.label} className="w-full h-full object-cover" />
                                                </div>
                                                <span className="text-xs font-bold text-slate-700 truncate">{preset.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {imageInputMode === 'url' && (
                                    <div className="relative">
                                        <LinkIcon size={16} className="absolute left-3.5 top-3.5 text-slate-400" />
                                        <input
                                            type="text"
                                            value={imageUrl}
                                            onChange={(e) => setImageUrl(e.target.value)}
                                            placeholder="https://example.com/banner-image.jpg"
                                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:outline-none focus:border-indigo-500"
                                        />
                                    </div>
                                )}

                                {/* Mobile Dedicated Banner Image */}
                                <div className="pt-3 border-t border-slate-200/80 space-y-2">
                                    <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                                        <span className="flex items-center gap-1.5">
                                            <Smartphone size={14} className="text-indigo-600" />
                                            모바일 전용 배너 이미지 (선택)
                                        </span>
                                        <span className="text-[11px] font-normal text-slate-400">미등록 시 PC 이미지가 자동 적용됩니다.</span>
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={mobileImageUrl}
                                            onChange={(e) => setMobileImageUrl(e.target.value)}
                                            placeholder="모바일용 세로/정사각형 이미지 URL 또는 파일 업로드"
                                            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:border-indigo-500"
                                        />
                                        <input
                                            ref={mobileFileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleMobileFileUpload}
                                            className="hidden"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => mobileFileInputRef.current?.click()}
                                            disabled={uploading}
                                            className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-xl transition-colors shrink-0 flex items-center gap-1.5"
                                        >
                                            <Upload size={14} /> 파일 선택
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Essential Inputs Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">배너 명칭 (관리용 이름) <span className="text-rose-500">*</span></label>
                                    <input
                                        type="text"
                                        required
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="예: 메인 배너 #1"
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:outline-none focus:border-indigo-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">배너 클릭 시 이동 링크</label>
                                    <input
                                        type="text"
                                        value={buttonLink}
                                        onChange={(e) => setButtonLink(e.target.value)}
                                        placeholder="inquiry (상담신청) 또는 이동 URL"
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:outline-none focus:border-indigo-500"
                                    />
                                </div>
                            </div>

                            {/* Optional YouTube Link for Banner */}
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5">
                                    <Youtube size={14} className="text-red-500" />
                                    <span>연계 유튜브 영상 링크 (선택)</span>
                                </label>
                                <input
                                    type="text"
                                    value={youtubeUrl}
                                    onChange={(e) => setYoutubeUrl(e.target.value)}
                                    placeholder="예: https://www.youtube.com/watch?v=... (선택 사항)"
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:outline-none focus:border-red-500"
                                />
                            </div>

                            {/* Additional Controls */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">노출 순서 (Order)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={order}
                                        onChange={(e) => setOrder(Number(e.target.value))}
                                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-indigo-500"
                                    />
                                </div>

                                <div className="flex flex-col justify-center">
                                    <label className="text-xs font-bold text-slate-700 mb-2">홈페이지 노출 상태</label>
                                    <label className="inline-flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={(e) => setIsActive(e.target.checked)}
                                            className="w-5 h-5 accent-indigo-600 rounded cursor-pointer"
                                        />
                                        <span className={`text-sm font-bold ${isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                                            {isActive ? '홈페이지 노출 중 (Active)' : '비노출 (Inactive)'}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-xl transition-colors"
                                >
                                    취소
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-sm rounded-xl shadow-lg shadow-indigo-200 transition-all"
                                >
                                    {editingBanner ? '수정사항 저장' : '배너 등록 완료'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BannersRoot;
